if (!customElements.get('country-selector')) {
  class CountrySelector extends HTMLElement {
    constructor() {
      super();
      this._onCountryChangedListener = this._onCountryChanged.bind(this);
    }

    connectedCallback() {
      this.countryElement = this.querySelector('[name="address[country]"]');
      this.provinceElement = this.querySelector('[name="address[province]"]');
      if (!this.countryElement || !this.provinceElement) return;

      this.countryElement.addEventListener('change', this._onCountryChangedListener);

      if (this.hasAttribute('country') && this.getAttribute('country') !== '') {
        this.countryElement.selectedIndex = Math.max(
          0,
          Array.from(this.countryElement.options).findIndex(
            (option) => option.textContent === this.getAttribute('country')
          )
        );
        this.countryElement.dispatchEvent(new Event('change'));
      } else {
        this._onCountryChanged();
      }
    }

    disconnectedCallback() {
      if (this.countryElement) {
        this.countryElement.removeEventListener('change', this._onCountryChangedListener);
      }
    }

    _onCountryChanged() {
      const option = this.countryElement.options[this.countryElement.selectedIndex];
      const provinces = JSON.parse(option.getAttribute('data-provinces') || '[]');
      const provinceWrapper =
        this.provinceElement.closest('.shipping-estimator__field--province') ||
        this.provinceElement.parentElement;

      provinceWrapper.hidden = provinces.length === 0;
      if (provinces.length === 0) {
        this.provinceElement.innerHTML = '';
        return;
      }

      this.provinceElement.innerHTML = '';
      provinces.forEach((data) => {
        const selected = data[1] === this.getAttribute('province');
        this.provinceElement.options.add(new Option(data[1], data[0], selected, selected));
      });
    }
  }

  customElements.define('country-selector', CountrySelector);
}

if (!customElements.get('shipping-estimator')) {
  class ShippingEstimator extends HTMLElement {
    constructor() {
      super();
      this._estimateShippingListener = this._estimateShipping.bind(this);
      this._onKeydownListener = this._onKeydown.bind(this);
    }

    connectedCallback() {
      this.submitButton = this.querySelector('.shipping-estimator__submit');
      this.resultsElement = this.querySelector('.shipping-estimator__results') || this.lastElementChild;
      this.formElement = this.querySelector('.shipping-estimator__form');

      if (this.formElement) {
        this.formElement.addEventListener('submit', this._estimateShippingListener);
        this.formElement.addEventListener('keydown', this._onKeydownListener);
        // Prevent cart-items quantity validation from handling estimator field changes.
        this.formElement.addEventListener('change', this._stopPropagation);
      } else if (this.submitButton) {
        this.submitButton.addEventListener('click', this._estimateShippingListener);
      }
    }

    disconnectedCallback() {
      if (this.formElement) {
        this.formElement.removeEventListener('submit', this._estimateShippingListener);
        this.formElement.removeEventListener('keydown', this._onKeydownListener);
        this.formElement.removeEventListener('change', this._stopPropagation);
      } else if (this.submitButton) {
        this.submitButton.removeEventListener('click', this._estimateShippingListener);
      }
    }

    _stopPropagation(event) {
      event.stopPropagation();
    }

    _onKeydown(event) {
      if (event.key === 'Enter') {
        event.preventDefault();
        event.stopPropagation();
        this._estimateShipping(event);
      }
    }

    async _estimateShipping(event) {
      event.preventDefault();
      event.stopPropagation();

      if (this.submitButton?.getAttribute('aria-busy') === 'true') return;

      const zip = this.querySelector('[name="address[zip]"]')?.value || '';
      const country = this.querySelector('[name="address[country]"]')?.value || '';
      const province = this.querySelector('[name="address[province]"]')?.value || '';
      const params = new URLSearchParams({
        'shipping_address[zip]': zip,
        'shipping_address[country]': country,
        'shipping_address[province]': province,
      });

      this.submitButton?.setAttribute('aria-busy', 'true');
      this.submitButton?.setAttribute('disabled', 'disabled');

      try {
        const prepareResponse = await fetch(
          `${window.Shopify.routes.root}cart/prepare_shipping_rates.json?${params.toString()}`,
          { method: 'POST' }
        );

        if (prepareResponse.ok) {
          const shippingRates = await this._getAsyncShippingRates(params);
          this._formatShippingRates(shippingRates);
        } else {
          const jsonError = await prepareResponse.json();
          this._formatError(jsonError);
        }
      } catch (error) {
        this._formatError({ error: error.message });
      }

      if (this.resultsElement) this.resultsElement.hidden = false;
      this.submitButton?.removeAttribute('aria-busy');
      this.submitButton?.removeAttribute('disabled');
    }

    async _getAsyncShippingRates(params) {
      const response = await fetch(
        `${window.Shopify.routes.root}cart/async_shipping_rates.json?${params.toString()}`
      );
      const responseAsText = await response.text();

      if (responseAsText === 'null') {
        return this._getAsyncShippingRates(params);
      }

      return JSON.parse(responseAsText).shipping_rates;
    }

    _formatShippingRates(shippingRates) {
      const strings = this._strings();
      const message =
        shippingRates.length === 0
          ? strings.noResults
          : shippingRates.length === 1
            ? strings.oneResult
            : strings.multipleResults;

      const listItems = shippingRates
        .map(
          (shippingRate) =>
            `<li>${shippingRate.presentment_name}: ${shippingRate.currency} ${shippingRate.price}</li>`
        )
        .join('');

      this.resultsElement.innerHTML = `
        <div class="shipping-estimator__results-inner">
          <p>${message}</p>
          ${listItems ? `<ul class="shipping-estimator__rates" role="list">${listItems}</ul>` : ''}
        </div>
      `;
    }

    _formatError(errors) {
      const strings = this._strings();
      const listItems = Object.keys(errors)
        .map((errorKey) => `<li>${errors[errorKey]}</li>`)
        .join('');

      this.resultsElement.innerHTML = `
        <div class="shipping-estimator__results-inner">
          <p>${strings.error}</p>
          <ul class="shipping-estimator__rates" role="list">${listItems}</ul>
        </div>
      `;
    }

    _strings() {
      return {
        noResults: this.dataset.noResults || '',
        oneResult: this.dataset.oneResult || '',
        multipleResults: this.dataset.multipleResults || '',
        error: this.dataset.error || '',
      };
    }
  }

  customElements.define('shipping-estimator', ShippingEstimator);
}
